<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    onClose: {
        type: Function,
        required: true,
    },
    position: {
        type: String,
        default: "center",
        validator: (value) => ["right", "center"].includes(value),
    },
    maxWidth: {
        type: String,
        default: "24rem",
    },
});

const dialogRef = ref(null);

const handleEscape = (e) => {
    if (e.key === "Escape" && props.isOpen) {
        props.onClose();
    }
};

watch(
    () => props.isOpen,
    (newValue) => {
        if (newValue) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
    }
);

onMounted(() => {
    document.addEventListener("keydown", handleEscape);
});

onBeforeUnmount(() => {
    document.removeEventListener("keydown", handleEscape);
    document.body.style.overflow = "";
});
</script>

<template>
    <Transition
        enter-active-class="transition ease-out duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition ease-in duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
    >
        <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
            <!-- Backdrop -->
            <div class="fixed inset-0 bg-black/25" @click="onClose" />

            <!-- Dialog positioning -->
            <div
                class="fixed inset-0 flex"
                :class="{
                    'justify-end': position === 'right',
                    'items-center justify-center': position === 'center',
                }"
            >
                <!-- Dialog panel -->
                <div
                    ref="dialogRef"
                    class="relative bg-white shadow-xl transition-all"
                    :class="{
                        'h-full w-full sm:max-w-[var(--dialog-max-width)]': position === 'right',
                        'w-full rounded-lg sm:max-w-[var(--dialog-max-width)]':
                            position === 'center',
                    }"
                    :style="{ '--dialog-max-width': maxWidth }"
                >
                    <slot />
                </div>
            </div>
        </div>
    </Transition>
</template>
